import { isFeatureEnabled } from 'helpers/featureHelpers'
import { createSelector } from 'reselect-change-memoize'
import { LOGIN_URL } from 'tangle-shared'
import { RETURN_URL_PARAM_NAME, PAGES_WITH_MAX_WIDTH } from 'constants/pages'
import * as lodashPick from 'lodash/pick'
import * as sortBy from 'lodash/sortBy'

export function createIsAnyPropChanged<A extends {}> (...props: (keyof A)[]) {
  return (obj1: A, obj2: A) =>
    props.some((prop) => obj1[prop] !== obj2[prop])
}

export const getCurrentUrl = () =>
  window.location.pathname

// TODO merge with prepareGetParams
export const encodeUrlParams = (params: Record<string, string | number | boolean | undefined>): string => {
  /*
    `params` like `{ a: 1, b: 2 }` and `{ b: 2, a: 2 }` yield different results
    therefore we need to sort the keys beforehand
  */
  const sortedKeys = sortBy(Object.keys(params))

  return sortedKeys.reduce<URLSearchParams>(
    (acc, key) => {
      if (params[key] !== undefined) {
        acc.append(key, String(params[key]))
      }

      return acc
    },
    new URLSearchParams,
  ).toString()
}

export const sendAnalytics = (...args: any[]) => {
  if (isFeatureEnabled('log')) {
    const [command, hitType, category, action] = args
    console.log(
      '[GA]',
      [command, hitType, category, action].filter(Boolean).join(' ')
    )
  }

  // TS doesn't understand ga(...args) for some reason
  ga.apply(ga, args)
}

export const createLoggedSelector = createSelector

type LocationBasedObj = Pick<Location, 'pathname' | 'hash' | 'search'>
export const makeUrlRelative = (absoluteUrl: string | LocationBasedObj): string => {
  const { pathname, search, hash } = typeof absoluteUrl === 'object' ? absoluteUrl : new URL(absoluteUrl)

  return `${ pathname }${ search }${ hash }`
}

export const getUrlComponents = (url: string) => {
  try {
    return new URL(url, document.baseURI)
  } catch (error) {
    return null // relative url was provided
  }
}

export const isPageWithMaxWidth = (location: Pick<Location, 'pathname'>) => {
  const checkRegExp = new RegExp(`^(${ PAGES_WITH_MAX_WIDTH.join('|') })(\/|$)`)
  return checkRegExp.test(location.pathname)
}

export const isLocalUrl = (urlComponents: URL): boolean => {
  return true
    && urlComponents.protocol === location.protocol
    && urlComponents.host === location.host
}

export const transformRelativeUrlToPathname = (relativeUrl: string) => {
  // Cut off the get-paremeters, hash and other rubbish
  return relativeUrl.replace(/^([^?]+)(.*)$/g, '$1')
}

export const getUnauthorizedRedirectUrl = () => {
  const relativeReturnUrl = makeUrlRelative(document.location.href)
  return `${ LOGIN_URL }?${ RETURN_URL_PARAM_NAME }=${ encodeURIComponent(relativeReturnUrl) }`
}

export const extractGetParam = (search: string, paramName: string): string | null => {
  const searchParams = new URLSearchParams(search)
  const paramValue = searchParams.get(paramName)

  return paramValue ? decodeURIComponent(paramValue) : null
}

export const extractAllGetParams = (search: string): Record<string, string> => {
  const result: Record<string, string> = {}

  new URLSearchParams(search).forEach((value, key) => {
    result[key] = value
  })

  return result
}

export function pick<T, K extends keyof T> (obj: T, keys: K[]): Pick<T, K> {
  return lodashPick(obj, keys)
}

export const isNullOrWhitespace = (input: unknown) =>
  input === undefined ||
  input === null ||
  (typeof input === 'string' && input.trim() === '')

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))


type AddScriptProps = {
  src: string
  id: string
  onLoad: () => void
}


// If you ever need to load a <script> in the head, this is an alternative.
// Make sure to removeScript() after
export const addScript = ({ src, id, onLoad }: AddScriptProps) => {
  const existing = document.getElementById(id)
  if (existing) {
    return existing
  } else {
    const script = document.createElement('script')
    script.src = src
    script.id = id
    script.async = true
    script.onload = () => {
      if (onLoad) {
        onLoad()
      }
    }
    document.body.appendChild(script)
    return script
  }
}

export const removeScript = ({ id }: { id: string }) => {
  const script = document.getElementById(id)
  if (script) {
    document.body.removeChild(script)
  }
}

